/**
 * Kart over en aktivitet (trening/tur): sporet farget etter fart, start (grønn)
 * og slutt (rød), på Kartverkets bakgrunnskart — samme kartlag som live-kartet.
 *
 * Reprise: båten seiler sporet på nytt, med fart, kurs og distanse underveis.
 * Tidslinjen er en fartsprofil i sporets farger — trykk eller dra for å hoppe.
 * Delen av sporet som ikke er seilt ennå, tones ned, og kartet følger båten.
 */
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { AktivitetSpor } from '../lib/api.ts';
import { bearingDeg, metersBetween } from '../lib/geo.ts';
import { disableWakeLock, enableWakeLock } from '../lib/wakelock.ts';
import { SAND } from './SiteLayout.tsx';

type Punkt = AktivitetSpor['points'][number];

const KV_ATTR = '© Kartverket';
const DEFAULT_CENTER: [number, number] = [58.34, 8.59];
const TZ = 'Europe/Oslo';
/** Hull i sporet (telefonen låst, appen i bakgrunnen): båten står stille, og avspillingen hopper over. */
const OPPHOLD_MS = 60_000;
const HASTIGHETER = [1, 10, 60, 300];
/** Oppløsning i fartsprofilen — tidslinjen er sjelden bredere enn dette i piksler. */
const PROFIL_BOLKER = 600;
const SPOR_OPACITY = 0.95;

/** Fart som andel av toppfarten: blå (rolig) → grønn → gul → rød (raskest). */
export const FARTSFARGER = ['#38bdf8', '#22c55e', '#eab308', '#ef4444'] as const;
const FARTSNAVN = ['rolig', 'middels', 'rask', 'raskest'];

function fargeFor(sog: number | null, maks: number): string {
  if (sog == null || maks <= 0) return FARTSFARGER[0];
  const r = sog / maks;
  return r < 0.3 ? FARTSFARGER[0] : r < 0.6 ? FARTSFARGER[1] : r < 0.85 ? FARTSFARGER[2] : FARTSFARGER[3];
}

const klokke = (ms: number, sekunder = false) =>
  new Date(ms).toLocaleTimeString('no-NO', {
    hour: '2-digit',
    minute: '2-digit',
    ...(sekunder ? { second: '2-digit' as const } : {}),
    timeZone: TZ,
  });

function fmtTid(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor((s % 3600) / 60);
  return `${Math.floor(s / 3600)}:${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

const fmtKn = (v: number | null) => (v == null ? '–' : `${v.toFixed(1).replace('.', ',')} kn`);
const fmtAvstand = (m: number) =>
  m < 185 ? `${Math.round(m)} m` : `${(m / 1852).toFixed(1).replace('.', ',')} nm`;
const fmtKurs = (deg: number) => `${String(Math.round(deg) % 360).padStart(3, '0')}°`;

interface Spor {
  t: number[];
  ll: [number, number][];
  sog: (number | null)[];
  /** Kurs (grader) for strekningen i → i+1, glattet over naboene. */
  kurs: number[];
  /** Seilt distanse (m) ved punkt i. */
  dist: number[];
  maks: number;
  /** Biter av sporet med samme fartsfarge (påfølgende biter deler endepunkt). */
  biter: { fra: number; til: number; farge: string }[];
}

function byggSpor(points: Punkt[], totalM: number | null | undefined): Spor {
  const n = points.length;
  const t = points.map((p) => Date.parse(p.recordedAt));
  const ll = points.map((p) => [p.lat, p.lon] as [number, number]);
  const sog = points.map((p) => p.sogKn);
  const maks = sog.reduce<number>((m, s) => Math.max(m, s ?? 0), 0);

  // Samme regel som serveren: sprang under 2 m er GPS-støy når båten ligger stille.
  const dist = new Array<number>(n).fill(0);
  let anker = 0;
  let sum = 0;
  for (let i = 1; i < n; i++) {
    const d = metersBetween(points[anker]!, points[i]!);
    if (d >= 2) {
      sum += d;
      anker = i;
    }
    dist[i] = sum;
  }
  // Skaler til serverens totale distanse, så reprisen ender på samme tall som nøkkeltallene.
  if (totalM && sum > 0) for (let i = 0; i < n; i++) dist[i] = (dist[i]! * totalM) / sum;

  // Kurs over et lite vindu rundt strekningen — ellers blir båten nervøs av GPS-støy.
  const kurs = new Array<number>(Math.max(0, n - 1)).fill(Number.NaN);
  for (let i = 0; i < n - 1; i++) {
    for (let w = 2; w <= 8; w++) {
      const a = points[Math.max(0, i + 1 - w)]!;
      const b = points[Math.min(n - 1, i + w)]!;
      if (metersBetween(a, b) >= 8) {
        kurs[i] = bearingDeg(a, b);
        break;
      }
    }
  }
  // Ligger båten stille, beholdes forrige kurs (og den første kjente helt i starten).
  let sist = kurs.find((k) => !Number.isNaN(k)) ?? 0;
  for (let i = 0; i < kurs.length; i++) {
    if (Number.isNaN(kurs[i])) kurs[i] = sist;
    else sist = kurs[i]!;
  }

  // Påfølgende strekninger med samme farge tegnes som én linje.
  const biter: Spor['biter'] = [];
  if (n > 1) {
    let fra = 0;
    let farge = fargeFor(sog[0] ?? null, maks);
    for (let i = 1; i < n; i++) {
      const f = fargeFor(sog[i] ?? null, maks);
      if (f !== farge || i === n - 1) {
        biter.push({ fra, til: i, farge });
        fra = i;
        farge = f;
      }
    }
  }
  return { t, ll, sog, kurs, dist, maks, biter };
}

/** Siste punkt med tid ≤ c. */
function indeksVed(t: number[], c: number): number {
  let lo = 0;
  let hi = t.length - 1;
  if (hi <= 0 || c <= t[0]!) return 0;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (t[mid]! <= c) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

/** Hvor langt (0–1) båten er kommet på strekningen i → i+1 ved tiden c; 0 i hull uten GPS. */
function andel(s: Spor, i: number, c: number): number {
  const a = s.t[i];
  const b = s.t[i + 1];
  if (a == null || b == null || b - a <= 0 || b - a > OPPHOLD_MS) return 0;
  return Math.min(1, Math.max(0, (c - a) / (b - a)));
}

function posVed(s: Spor, i: number, c: number): [number, number] {
  const a = s.ll[i]!;
  const f = andel(s, i, c);
  if (f === 0) return a;
  const b = s.ll[i + 1]!;
  return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f];
}

function fartVed(s: Spor, i: number, c: number): number | null {
  const a = s.sog[i] ?? null;
  const b = s.sog[i + 1] ?? null;
  if (a == null || b == null) return a ?? b;
  return a + (b - a) * andel(s, i, c);
}

function distVed(s: Spor, i: number, c: number): number {
  const a = s.dist[i] ?? 0;
  const b = s.dist[i + 1];
  return b == null ? a : a + (b - a) * andel(s, i, c);
}

/**
 * Fartsprofil som SVG-stier (0–1000 bredt, 0–100 høyt; topp = toppfarten), farget som
 * sporet på kartet. Høyeste fart per bolk, så toppene på kartet også synes her; ved
 * hull uten GPS brytes profilen.
 */
function lagProfil(s: Spor): { farge: string; flate: string; kant: string }[] {
  const n = s.t.length;
  if (n < 2) return [];
  const t0 = s.t[0]!;
  const span = s.t[n - 1]! - t0 || 1;
  const topp = s.maks > 0 ? s.maks : 1;
  const bolker: { b: number; v: number; fra: number; til: number }[] = [];
  for (let i = 0; i < n; i++) {
    const t = s.t[i]!;
    const b = Math.min(PROFIL_BOLKER - 1, Math.floor(((t - t0) / span) * PROFIL_BOLKER));
    const v = s.sog[i] ?? 0;
    const siste = bolker[bolker.length - 1];
    if (siste && siste.b === b) {
      siste.v = Math.max(siste.v, v);
      siste.til = t;
    } else bolker.push({ b, v, fra: t, til: t });
  }

  // Påfølgende bolker med samme farge blir én flate; flatene deler endepunkt (ikke over hull).
  const deler: { farge: string; pkt: string[] }[] = [];
  let forrige: (typeof bolker)[number] | null = null;
  for (const bo of bolker) {
    const p = `${(((bo.b + 0.5) / PROFIL_BOLKER) * 1000).toFixed(1)},${(100 - (Math.min(bo.v, topp) / topp) * 100).toFixed(1)}`;
    const farge = fargeFor(bo.v, s.maks);
    const hull = !!forrige && bo.fra - forrige.til > OPPHOLD_MS;
    const del = deler[deler.length - 1];
    if (!del || hull || del.farge !== farge) {
      deler.push({ farge, pkt: del && !hull ? [del.pkt[del.pkt.length - 1]!, p] : [p] });
    } else del.pkt.push(p);
    forrige = bo;
  }
  return deler.map(({ farge, pkt }) => {
    const kant = `M${pkt.join(' L')}`;
    const x = (q: string) => q.slice(0, q.indexOf(','));
    return { farge, kant, flate: `M${x(pkt[0]!)},100 L${pkt.join(' L')} L${x(pkt[pkt.length - 1]!)},100 Z` };
  });
}

/** Båten i reprisen: skrog som dreies etter kursen, med farten ved siden av. */
function baatIkon(): L.DivIcon {
  return L.divIcon({
    className: '',
    iconSize: [0, 0],
    iconAnchor: [0, 0],
    html:
      `<div style="position:relative">` +
      `<div data-skrog style="position:absolute;left:-13px;top:-13px;width:26px;height:26px">` +
      `<svg viewBox="0 0 24 24" width="26" height="26" style="display:block;filter:drop-shadow(0 0 2px rgba(0,0,0,.9))">` +
      `<path d="M12 1.5C16.2 6.5 17.4 13 16.4 22.5H7.6C6.6 13 7.8 6.5 12 1.5Z" fill="${SAND}" stroke="#fff" stroke-width="1.6" stroke-linejoin="round"/>` +
      `</svg></div>` +
      `<span data-fart style="position:absolute;left:15px;top:-10px;white-space:nowrap;font:600 11px/18px system-ui,sans-serif;color:#fff;background:rgba(15,23,42,.85);border:1px solid rgba(148,163,184,.45);padding:0 5px;border-radius:6px"></span>` +
      `</div>`,
  });
}

interface Kart {
  map: L.Map;
  linjer: { fra: number; til: number; linje: L.Polyline; vis: 'hel' | 'del' | 'skjult' }[];
  baat: L.Marker | null;
  bounds: L.LatLngBounds | null;
  /** Kartet flyttes/zoomes (av brukeren eller av oss) — da lar vi det være i fred. */
  flytter: boolean;
}

export function ActivityMap({
  points,
  totalM,
  tittel,
}: {
  points: Punkt[];
  /** Serverens totale distanse — distansen i reprisen skaleres til den. */
  totalM?: number | null;
  /** Vises øverst i fullskjerm. */
  tittel?: string;
}) {
  const spor = useMemo(() => byggSpor(points, totalM), [points, totalM]);
  const profil = useMemo(() => lagProfil(spor), [spor]);
  const n = spor.t.length;
  const t0 = spor.t[0] ?? 0;
  const t1 = spor.t[n - 1] ?? 0;
  const kanSpilles = n >= 2 && t1 > t0;

  const el = useRef<HTMLDivElement | null>(null);
  const kart = useRef<Kart | null>(null);
  const [cursor, setCursor] = useState(t0);
  const cursorRef = useRef(t0);
  const [aktiv, setAktiv] = useState(false);
  const [spiller, setSpiller] = useState(false);
  const [hastighet, setHastighet] = useState(60);
  const [fullskjerm, setFullskjerm] = useState(false);
  const drag = useRef<{ id: number; x: number; flyttet: boolean; spilte: boolean } | null>(null);

  function flytt(c: number) {
    cursorRef.current = c;
    setCursor(c);
  }

  // Kartet og sporet
  useEffect(() => {
    if (!el.current) return;
    // SVG, ikke lerret: Leaflets lerret kan tegne på nytt etter at kartet er fjernet.
    const map = L.map(el.current, { zoomControl: true });
    const graatone = L.tileLayer(
      'https://cache.kartverket.no/v1/wmts/1.0.0/topograatone/default/webmercator/{z}/{y}/{x}.png',
      { maxZoom: 18, attribution: KV_ATTR },
    );
    const sjokart = L.tileLayer(
      'https://cache.kartverket.no/v1/wmts/1.0.0/sjokartraster/default/webmercator/{z}/{y}/{x}.png',
      { maxZoom: 18, attribution: KV_ATTR },
    );
    graatone.addTo(map);
    L.control.layers({ Gråtone: graatone, Sjøkart: sjokart }, {}, { collapsed: true }).addTo(map);

    const k: Kart = { map, linjer: [], baat: null, bounds: null, flytter: false };
    if (n === 0) {
      map.setView(DEFAULT_CENTER, 11);
    } else {
      // Hele sporet svakt i bunnen — det er dette som synes foran båten i reprisen.
      if (n > 1) L.polyline(spor.ll, { color: '#64748b', weight: 3, opacity: 0.7, interactive: false }).addTo(map);
      for (const b of spor.biter) {
        const linje = L.polyline(spor.ll.slice(b.fra, b.til + 1), {
          color: b.farge,
          weight: 4,
          opacity: SPOR_OPACITY,
          interactive: false,
        }).addTo(map);
        k.linjer.push({ fra: b.fra, til: b.til, linje, vis: 'hel' });
      }
      const prikk = (pos: [number, number], fill: string, tekst: string) =>
        L.circleMarker(pos, { radius: 7, color: '#ffffff', weight: 2, fillColor: fill, fillOpacity: 1 })
          .bindTooltip(tekst)
          .addTo(map);
      prikk(spor.ll[0]!, '#22c55e', `Start ${klokke(spor.t[0]!)}`);
      if (n > 1) prikk(spor.ll[n - 1]!, '#ef4444', `Slutt ${klokke(spor.t[n - 1]!)}`);
      k.baat = L.marker(spor.ll[0]!, {
        icon: baatIkon(),
        interactive: false,
        keyboard: false,
        zIndexOffset: 1000,
      });
      k.bounds = L.latLngBounds(spor.ll);
      map.fitBounds(k.bounds, { padding: [24, 24], maxZoom: 16 });
    }
    map.on('movestart', () => {
      k.flytter = true;
    });
    map.on('moveend', () => {
      k.flytter = false;
    });
    kart.current = k;

    const node = el.current;
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(node);
    const t = setTimeout(() => map.invalidateSize(), 100);
    return () => {
      clearTimeout(t);
      ro.disconnect();
      map.remove();
      kart.current = null;
    };
  }, [spor, n]);

  // Nytt spor (f.eks. en pågående aktivitet som er lastet på nytt) — start reprisen på nytt.
  useEffect(() => {
    flytt(t0);
    setAktiv(false);
    setSpiller(false);
  }, [spor, t0]);

  // Båten og sporet ved tidspunktet i reprisen
  useEffect(() => {
    const k = kart.current;
    if (!k || n < 2) return;
    const i = aktiv ? indeksVed(spor.t, cursor) : n - 1;
    const pos = aktiv ? posVed(spor, i, cursor) : spor.ll[n - 1]!;
    // Bitene skjules med opacity (ikke fjernes), så rekkefølgen — og start/slutt øverst — beholdes.
    for (const l of k.linjer) {
      if (l.til <= i) {
        if (l.vis !== 'hel') {
          l.linje.setLatLngs(spor.ll.slice(l.fra, l.til + 1));
          if (l.vis === 'skjult') l.linje.setStyle({ opacity: SPOR_OPACITY });
          l.vis = 'hel';
        }
      } else if (l.fra <= i) {
        l.linje.setLatLngs([...spor.ll.slice(l.fra, i + 1), pos]);
        if (l.vis === 'skjult') l.linje.setStyle({ opacity: SPOR_OPACITY });
        l.vis = 'del';
      } else if (l.vis !== 'skjult') {
        l.linje.setStyle({ opacity: 0 });
        l.vis = 'skjult';
      }
    }

    const baat = k.baat;
    if (!baat) return;
    if (!aktiv) {
      baat.remove();
      return;
    }
    baat.setLatLng(pos);
    if (!k.map.hasLayer(baat)) baat.addTo(k.map);
    const elm = baat.getElement();
    const skrog = elm?.querySelector<HTMLElement>('[data-skrog]');
    if (skrog) skrog.style.transform = `rotate(${spor.kurs[Math.min(i, n - 2)] ?? 0}deg)`;
    const fart = elm?.querySelector<HTMLElement>('[data-fart]');
    if (fart) fart.textContent = fmtKn(fartVed(spor, i, cursor));

    // Følg båten: flytt kartet når den nærmer seg kanten.
    if (!k.flytter) {
      const p = k.map.latLngToContainerPoint(pos);
      const s = k.map.getSize();
      const m = 16;
      if (p.x < m || p.y < m || p.x > s.x - m || p.y > s.y - m) k.map.panTo(pos);
    }
  }, [cursor, aktiv, spor, n]);

  // Avspilling — jevn bevegelse med requestAnimationFrame.
  useEffect(() => {
    if (!spiller) return;
    let raf = 0;
    let forrige = performance.now();
    const tick = (na: number) => {
      const dt = Math.min(na - forrige, 250); // fanen i bakgrunnen: ikke hopp langt frem
      forrige = na;
      let neste = cursorRef.current + dt * hastighet;
      const i = indeksVed(spor.t, neste);
      if (i < n - 1 && spor.t[i + 1]! - spor.t[i]! > OPPHOLD_MS) neste = spor.t[i + 1]!;
      if (neste >= t1) {
        flytt(t1);
        setSpiller(false);
        return;
      }
      flytt(neste);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [spiller, hastighet, spor, n, t1]);

  // Hold skjermen våken mens reprisen spiller.
  useEffect(() => {
    if (!spiller) return;
    void enableWakeLock();
    return () => {
      void disableWakeLock();
    };
  }, [spiller]);

  // Fullskjerm: Esc lukker, siden bak scroller ikke.
  useEffect(() => {
    if (!fullskjerm) return;
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFullskjerm(false);
    };
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', esc);
    return () => {
      document.body.style.overflow = overflow;
      window.removeEventListener('keydown', esc);
    };
  }, [fullskjerm]);

  // Ny kartstørrelse ved fullskjerm av/på — vis hele sporet igjen.
  useEffect(() => {
    const k = kart.current;
    if (!k) return;
    k.map.invalidateSize();
    if (k.bounds) k.map.fitBounds(k.bounds, { padding: [24, 24], maxZoom: 16 });
  }, [fullskjerm]);

  function spillPause() {
    if (spiller) {
      setSpiller(false);
      return;
    }
    if (!aktiv || cursorRef.current >= t1) flytt(t0);
    setAktiv(true);
    setSpiller(true);
  }

  function hopp(c: number) {
    setAktiv(true);
    flytt(Math.min(t1, Math.max(t0, c)));
  }

  function tidVed(e: ReactPointerEvent<HTMLDivElement>): number {
    const r = e.currentTarget.getBoundingClientRect();
    const f = r.width > 0 ? (e.clientX - r.left) / r.width : 0;
    return t0 + Math.min(1, Math.max(0, f)) * (t1 - t0);
  }

  // Mus: hopp med en gang. Berøring: først ved sideveis dra eller slipp, så siden
  // fortsatt kan scrolles med en loddrett sveip over tidslinjen.
  function pekerNed(e: ReactPointerEvent<HTMLDivElement>) {
    const mus = e.pointerType !== 'touch';
    drag.current = { id: e.pointerId, x: e.clientX, flyttet: mus, spilte: spiller };
    e.currentTarget.setPointerCapture(e.pointerId);
    setSpiller(false);
    if (mus) hopp(tidVed(e));
  }
  function pekerFlytt(e: ReactPointerEvent<HTMLDivElement>) {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    if (!d.flyttet && Math.abs(e.clientX - d.x) < 4) return;
    d.flyttet = true;
    hopp(tidVed(e));
  }
  function pekerOpp(e: ReactPointerEvent<HTMLDivElement>) {
    const d = drag.current;
    drag.current = null;
    if (!d || d.id !== e.pointerId) return;
    hopp(tidVed(e));
    if (d.spilte) setSpiller(true);
  }
  function pekerAvbrutt() {
    const d = drag.current;
    drag.current = null;
    if (d?.spilte) setSpiller(true);
  }

  function tast(e: ReactKeyboardEvent<HTMLDivElement>) {
    const steg = e.shiftKey ? 60_000 : 10_000;
    if (e.key === 'ArrowRight') hopp(cursorRef.current + steg);
    else if (e.key === 'ArrowLeft') hopp(cursorRef.current - steg);
    else if (e.key === 'Home') hopp(t0);
    else if (e.key === 'End') hopp(t1);
    else if (e.key === ' ' || e.key === 'Enter') spillPause();
    else return;
    e.preventDefault();
  }

  const i = indeksVed(spor.t, cursor);
  const andelSpilt = kanSpilles ? Math.min(1, Math.max(0, (cursor - t0) / (t1 - t0))) : 0;
  const venstre = `${andelSpilt * 100}%`;

  return (
    <div
      className={fullskjerm ? 'fixed inset-0 z-[1100] bg-slate-950 flex flex-col gap-2 p-2 sm:p-4' : 'space-y-2'}
      style={fullskjerm ? { paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' } : undefined}
    >
      {fullskjerm && (
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-semibold truncate">⏮ Reprise{tittel ? ` — ${tittel}` : ''}</h2>
          <button
            onClick={() => setFullskjerm(false)}
            className="text-sm px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 shrink-0"
          >
            ✕ Lukk
          </button>
        </div>
      )}

      <div
        className={`relative w-full rounded-xl overflow-hidden border border-slate-800 ${
          fullskjerm ? 'flex-1 min-h-0' : 'h-80'
        }`}
      >
        <div ref={el} className="absolute inset-0" />
      </div>

      {kanSpilles && (
        <>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 min-h-[1.5rem] text-sm">
            {aktiv ? (
              <>
                <span className="font-mono tabular-nums font-semibold text-slate-100">{klokke(cursor, true)}</span>
                <span className="font-mono tabular-nums text-xs text-slate-500" title="Tid siden start">
                  +{fmtTid(cursor - t0)}
                </span>
                <span className="ml-auto flex gap-3 font-mono tabular-nums">
                  <span className="font-semibold text-slate-100" title="Fart over grunn">
                    {fmtKn(fartVed(spor, i, cursor))}
                  </span>
                  <span className="text-slate-300" title="Kurs over grunn">
                    {fmtKurs(spor.kurs[Math.min(i, n - 2)] ?? 0)}
                  </span>
                  <span className="text-slate-300" title="Seilt så langt">
                    {fmtAvstand(distVed(spor, i, cursor))}
                  </span>
                </span>
              </>
            ) : (
              <span className="text-xs text-slate-400">
                Spill av turen: båten seiler sporet på nytt, med fart, kurs og distanse underveis.
              </span>
            )}
          </div>

          <div>
            <div
              role="slider"
              tabIndex={0}
              aria-label="Tidslinje for reprisen"
              aria-valuemin={0}
              aria-valuemax={Math.round((t1 - t0) / 1000)}
              aria-valuenow={Math.round((cursor - t0) / 1000)}
              aria-valuetext={klokke(cursor, true)}
              title="Trykk eller dra for å hoppe i reprisen"
              onPointerDown={pekerNed}
              onPointerMove={pekerFlytt}
              onPointerUp={pekerOpp}
              onPointerCancel={pekerAvbrutt}
              onKeyDown={tast}
              className="relative h-12 rounded-lg bg-slate-950/60 border border-slate-800 overflow-hidden cursor-pointer select-none outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
              style={{ touchAction: 'pan-y' }}
            >
              <svg
                viewBox="0 -6 1000 106"
                preserveAspectRatio="none"
                className="absolute inset-0 w-full h-full"
                aria-hidden="true"
              >
                {profil.map((d, j) => (
                  <g key={j}>
                    <path d={d.flate} fill={d.farge} fillOpacity={0.3} />
                    <path
                      d={d.kant}
                      fill="none"
                      stroke={d.farge}
                      strokeWidth={1.5}
                      strokeLinejoin="round"
                      vectorEffect="non-scaling-stroke"
                    />
                  </g>
                ))}
              </svg>
              {aktiv && <div className="absolute inset-y-0 right-0 bg-slate-950/55" style={{ left: venstre }} />}
              <div
                className="absolute inset-y-0 w-0.5 -ml-px bg-white shadow-[0_0_4px_rgba(0,0,0,0.9)]"
                style={{ left: venstre }}
              />
            </div>
            <div className="flex justify-between mt-0.5 text-[11px] text-slate-500">
              <span className="font-mono tabular-nums">{klokke(t0)}</span>
              <span>fartsprofil · topp {fmtKn(spor.maks || null)}</span>
              <span className="font-mono tabular-nums">{klokke(t1)}</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={spillPause}
              className="w-28 px-3 py-2 bg-sky-600 hover:bg-sky-500 rounded-lg font-semibold text-sm shrink-0"
            >
              {spiller ? '⏸ Pause' : aktiv && cursor < t1 ? '▶ Fortsett' : '▶ Spill av'}
            </button>
            <div className="flex items-center gap-1 text-xs" role="group" aria-label="Avspillingshastighet">
              {HASTIGHETER.map((h) => (
                <button
                  key={h}
                  onClick={() => setHastighet(h)}
                  aria-pressed={hastighet === h}
                  className={`px-2 py-1 rounded ${
                    hastighet === h ? 'bg-sky-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                  }`}
                >
                  {h}×
                </button>
              ))}
            </div>
            <button
              onClick={() => setFullskjerm((f) => !f)}
              className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-xs text-slate-200"
              title={fullskjerm ? 'Lukk fullskjerm' : 'Vis reprisen i fullskjerm'}
            >
              <svg
                viewBox="0 0 24 24"
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path
                  d={
                    fullskjerm
                      ? 'M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5'
                      : 'M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5'
                  }
                />
              </svg>
              <span className="hidden sm:inline">{fullskjerm ? 'Lukk' : 'Fullskjerm'}</span>
            </button>
          </div>
        </>
      )}

      {n > 1 && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-400">
          <span>Fart:</span>
          {FARTSFARGER.map((c, j) => (
            <span key={c} className="inline-flex items-center gap-1">
              <span className="w-3 h-1.5 rounded" style={{ background: c }} />
              {FARTSNAVN[j]}
            </span>
          ))}
          <span className="text-slate-500">· farget etter andel av toppfarten</span>
        </div>
      )}
    </div>
  );
}
